// Phase 41 Plan 41-08 — API client for the SLA Task timer snapshot.
//
// Wire shape mirrors backend `SlaTimerSnapshotDto` from
// `backend/src/services/slaTaskTimerService.ts:60-73` (frozen Phase 38 ABI).
// Verified against `backend/src/api/schemas/slaTaskTimer.ts` (Pitfall 3 /
// fast-json-stringify): all 12 fields below appear in BOTH `properties`
// AND `required` on the backend response schema, so the wire will not
// silently strip any of them. `additionalProperties: false` further
// rejects accidental serializer overshoot.
//
// Endpoint mounted via `backend/src/api/routes/slaTaskTimerRoutes.ts:24`
// at GET /api/tasks/:taskId/sla-timer, gated by
// `requireAdminOrProjectMemberByTask` — only admins / PMs / project
// members receive 200 (T-41-22 mitigation: defense in depth — UI also
// hides the bar when the request 404s/403s).
//
// **Pitfall 3 — Backend gap noted (Phase 41 milestone-end backlog):**
// The shipped DTO does NOT include `currentStateEffect` (e.g. 'waiting')
// or per-tier `warningThresholds[]`. Both are derivable backend-side but
// were not surfaced in Phase 38 (only `isInTerminalState` is exposed).
// The frontend display interface (`SLATimerBarInput` in
// `frontend/src/components/sla/SLATimerBar.tsx`) is shaped to render
// these states when the data is available; the wrapper component
// (`SlaTaskCard.tsx`) degrades by passing `currentStateEffect=null` and
// `warningThresholds=[]` until the backend gap is closed. See
// 41-08-SUMMARY.md "Pitfall 3 Backlog" section for the recommended
// /gsd-quick.

import { apiGet } from './client';

export interface SlaTaskTimerSnapshot {
  taskId: number;
  projectId: number;
  tierId: number | null;
  /** Elapsed milliseconds for the response (R) timer; capped at first response_stopped transition. */
  responseElapsedMs: number;
  /** Elapsed milliseconds for the resolution (X) timer; accrues across Rework cycles. */
  resolutionElapsedMs: number;
  /** Raw wall-clock time spent in waiting state (TASK-07 paused-on-customer counter). */
  waitingTotalMs: number;
  /** Tier-configured response target in minutes (null when task has no tier). */
  responseTargetMinutes: number | null;
  /** Tier-configured resolution target in minutes (null when task has no tier). */
  resolutionTargetMinutes: number | null;
  /** Pre-computed percentage = elapsedMs / (targetMinutes * 60_000) * 100 (null when no tier). */
  responsePercentage: number | null;
  /** Pre-computed percentage = elapsedMs / (targetMinutes * 60_000) * 100 (null when no tier). */
  resolutionPercentage: number | null;
  /** ISO timestamp of the first response_stopped transition; null while still running. */
  responseStoppedAt: string | null;
  /** True when the task's current state's slaTimerEffect === 'terminal'. */
  isInTerminalState: boolean;
}

export function fetchSlaTaskTimer(taskId: number) {
  return apiGet<{ data: SlaTaskTimerSnapshot }>(`/tasks/${taskId}/sla-timer`);
}
